export type ScaledUnitCost = {
  /** e.g. `Cost / 100 Calls` or `Cost / Call` */
  label: string
  /** e.g. `$1.75` */
  value: string
}

const SCALES = [1, 100, 1000] as const

/** Placeholder for "nothing was spent here", used everywhere in place of `$0.00`. */
export const NO_COST = `—`

const USD_FORMATTER = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})

/**
 * Format a raw dollar amount with exactly two decimals and thousands
 * separators. Returns `$0.00` for a non-finite value.
 */
export function formatUsd(value: number): string {
  if (!Number.isFinite(value)) return `$0.00`
  return USD_FORMATTER.format(value)
}

/**
 * Product owner directive: per-unit costs are the headline metric, but penny
 * amounts are unreadable. Scale by 1, 100, or 1,000 units — the smallest
 * scale whose displayed value is at least $1.
 */
export function formatScaledUnitCost(costPerUnit: number, unitSingular: string, unitPlural: string): ScaledUnitCost {
  if (!Number.isFinite(costPerUnit) || costPerUnit <= 0) {
    return { label: `Cost / ${unitSingular}`, value: NO_COST }
  }

  const scale = SCALES.find((candidate) => costPerUnit * candidate >= 1) ?? 1000

  const label = scale === 1 ? `Cost / ${unitSingular}` : `Cost / ${scale.toLocaleString('en-US')} ${unitPlural}`

  return { label, value: formatUsd(costPerUnit * scale) }
}

/** Render a latency in the largest unit that stays readable: `840ms`, `1.2s`. */
export function formatLatencyMs(ms: number): string {
  if (!Number.isFinite(ms)) return `—`
  if (ms < 1000) return `${Math.round(ms)}ms`
  return `${(ms / 1000).toFixed(1)}s`
}

const PRECISE_USD_FORMATTER = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 4,
})

/**
 * Individual calls and SMS threads cost fractions of a cent, so the two-decimal
 * `formatUsd` collapses a whole drill-down list to `$0.00`. Show up to four
 * decimals, but only where they carry information — amounts of a cent or more
 * keep the familiar two-decimal form.
 */
export function formatUsdPrecise(value: number): string {
  if (!Number.isFinite(value)) return `$0.00`
  if (value === 0 || Math.abs(value) >= 0.01) return formatUsd(value)
  return PRECISE_USD_FORMATTER.format(value)
}

/**
 * The single money formatter for usage tracking. Nothing spent is rendered as an em
 * dash — a grid of `$0.00` reads as "we measured zero" when it means "no records" — and
 * every non-zero amount goes through `formatUsdPrecise`, so rounding is identical
 * wherever a cost appears (cards, tables, chart tooltip, legend).
 */
export function formatUsdOrDash(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value) || value === 0) return NO_COST
  return formatUsdPrecise(value)
}
